/**
 * DevPhotoInspectorPage
 *
 * Development tool for inspecting photos in detail.
 * Shows list of user's photos on left, detailed inspection view on right.
 *
 * Routes:
 * - /dev/tool/photos - List view with no selection
 * - /dev/tool/photos/:photoId - List view with selected photo detail
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SplitPane, Panel, PanelResizeHandle } from '@/components/layout';
import { CollectionAndDetailView2 } from '@/components/CollectionAndDetailView2';
import { usePhotosGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { usePhotoInspectionGraphQL } from '@/graphql/hooks/usePhotoInspectionGraphQL';
import { useRefreshPhotoMetadataGraphQL } from '@/graphql/hooks/useRefreshPhotoMetadataGraphQL';
import { VersionHistory } from '@/components/VersionHistory';
import { ImageCompare } from '@/components/ImageCompare';
import { usePhotoVersions } from '@/hooks/graphql/usePhotoVersionsGraphQL';
import logger from '@/utils/logger';

function PhotoDetailEmpty() {
  return (
    <div className="h-full flex items-center justify-center text-muted">
      <div className="text-center">
        <div className="text-6xl mb-4">👈</div>
        <p className="text-lg font-medium mb-2">Select a photo to inspect</p>
        <p className="text-sm">Choose from your photos on the left</p>
      </div>
    </div>
  );
}

// Timeout durations for status reset
const SUCCESS_TOAST_DURATION = 3000;
const ERROR_TOAST_DURATION = 5000;

function PhotoDetailView({ photoId }: { photoId: string }) {
  const { inspection, loading, error, refetch } = usePhotoInspectionGraphQL(photoId);
  const { refreshMetadata, loading: refreshing } = useRefreshPhotoMetadataGraphQL();
  const { versions } = usePhotoVersions(photoId);
  const [refreshStatus, setRefreshStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showCompare, setShowCompare] = useState(false);

  // Track mounted state and timeout for cleanup
  const mountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleRefreshExif = async () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setRefreshStatus('idle');
    try {
      logger.info('PhotoInspector', 'Refreshing EXIF metadata', { photoId });
      await refreshMetadata(photoId);
      await refetch();

      if (!mountedRef.current) return;

      setRefreshStatus('success');
      logger.info('PhotoInspector', 'EXIF metadata refreshed successfully', { photoId });

      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          setRefreshStatus('idle');
        }
      }, SUCCESS_TOAST_DURATION);
    } catch (err) {
      if (!mountedRef.current) return;

      logger.error('PhotoInspector', 'Failed to refresh EXIF metadata', { photoId, error: err });
      setRefreshStatus('error');

      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          setRefreshStatus('idle');
        }
      }, ERROR_TOAST_DURATION);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted">Loading inspection data...</div>
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-medium text-destructive mb-2">Error loading photo</p>
          <p className="text-sm text-muted">Could not fetch inspection data for {photoId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <h2 className="text-2xl font-bold mb-6">{inspection.photo.title}</h2>

      {/* Basic Info */}
      <section className="mb-8">
        <h3 className="text-lg font-semibold mb-3">Basic Info</h3>
        <div className="bg-surface-2 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-muted">Global ID:</span>
            <span className="font-mono">{inspection.photo.id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Slug:</span>
            <span>{inspection.photo.slug}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Title:</span>
            <span>{inspection.photo.title}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-muted">Description:</span>
            {inspection.photo.description ? (
              <span className="text-sm pl-4">{inspection.photo.description}</span>
            ) : (
              <span className="text-sm pl-4 italic text-muted/60">— empty —</span>
            )}
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Owner:</span>
            <span>@{inspection.owner.username}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Uploaded:</span>
            <span>{new Date(inspection.photo.createdAt).toLocaleString()}</span>
          </div>
        </div>
      </section>

      {/* Storage & Files */}
      <section className="mb-8">
        <h3 className="text-lg font-semibold mb-3">Storage & Files</h3>
        <div className="bg-surface-2 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-muted">Filename:</span>
            <span className="font-mono text-sm">{inspection.photo.filename}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">MIME Type:</span>
            <span>{inspection.photo.mimeType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">File Size:</span>
            <span>{(inspection.photo.fileSize / 1024 / 1024).toFixed(2)} MB</span>
          </div>
          {inspection.photo.fileHash && (
            <div className="flex justify-between">
              <span className="text-muted">File Hash:</span>
              <span className="font-mono text-xs">{inspection.photo.fileHash.substring(0, 16)}...</span>
            </div>
          )}
        </div>
      </section>

      {/* Metadata (EXIF) */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Metadata (EXIF)</h3>
          <button
            onClick={handleRefreshExif}
            disabled={refreshing}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors
              ${refreshing
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : refreshStatus === 'success'
                  ? 'bg-green-600 text-white'
                  : refreshStatus === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
          >
            {refreshing
              ? 'Refreshing...'
              : refreshStatus === 'success'
                ? 'Refreshed!'
                : refreshStatus === 'error'
                  ? 'Failed'
                  : 'Refresh EXIF'}
          </button>
        </div>
        {inspection.photo.metadata ? (
          <div className="bg-surface-2 rounded-lg p-4 space-y-4">
            {/* Camera */}
            {inspection.photo.metadata.camera && Object.keys(inspection.photo.metadata.camera).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Camera</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.camera.make && (
                    <div className="flex justify-between">
                      <span className="text-muted">Make:</span>
                      <span>{inspection.photo.metadata.camera.make}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.camera.model && (
                    <div className="flex justify-between">
                      <span className="text-muted">Model:</span>
                      <span>{inspection.photo.metadata.camera.model}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.camera.serialNumber && (
                    <div className="flex justify-between">
                      <span className="text-muted">Serial Number:</span>
                      <span className="font-mono text-xs">{inspection.photo.metadata.camera.serialNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lens */}
            {inspection.photo.metadata.lens && Object.keys(inspection.photo.metadata.lens).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Lens</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.lens.make && (
                    <div className="flex justify-between">
                      <span className="text-muted">Make:</span>
                      <span>{inspection.photo.metadata.lens.make}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.lens.model && (
                    <div className="flex justify-between">
                      <span className="text-muted">Model:</span>
                      <span>{inspection.photo.metadata.lens.model}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.lens.serialNumber && (
                    <div className="flex justify-between">
                      <span className="text-muted">Serial Number:</span>
                      <span className="font-mono text-xs">{inspection.photo.metadata.lens.serialNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Exposure */}
            {inspection.photo.metadata.exposure && Object.keys(inspection.photo.metadata.exposure).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Exposure</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.exposure.iso !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">ISO:</span>
                      <span>{inspection.photo.metadata.exposure.iso}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.exposure.fNumber !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">Aperture:</span>
                      <span>f/{inspection.photo.metadata.exposure.fNumber}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.exposure.exposureTime && (
                    <div className="flex justify-between">
                      <span className="text-muted">Shutter Speed:</span>
                      <span>{inspection.photo.metadata.exposure.exposureTime}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.exposure.exposureCompensation !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">Exposure Compensation:</span>
                      <span>{inspection.photo.metadata.exposure.exposureCompensation > 0 ? '+' : ''}{inspection.photo.metadata.exposure.exposureCompensation} EV</span>
                    </div>
                  )}
                  {inspection.photo.metadata.exposure.meteringMode && (
                    <div className="flex justify-between">
                      <span className="text-muted">Metering Mode:</span>
                      <span>{inspection.photo.metadata.exposure.meteringMode}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Focus */}
            {inspection.photo.metadata.focus && Object.keys(inspection.photo.metadata.focus).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Focus</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.focus.focalLength !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">Focal Length:</span>
                      <span>{inspection.photo.metadata.focus.focalLength}mm</span>
                    </div>
                  )}
                  {inspection.photo.metadata.focus.focalLength35mm !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">Focal Length (35mm equiv):</span>
                      <span>{inspection.photo.metadata.focus.focalLength35mm}mm</span>
                    </div>
                  )}
                  {inspection.photo.metadata.focus.focusMode && (
                    <div className="flex justify-between">
                      <span className="text-muted">Focus Mode:</span>
                      <span>{inspection.photo.metadata.focus.focusMode}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Capture */}
            {inspection.photo.metadata.capture && Object.keys(inspection.photo.metadata.capture).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Capture</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.capture.dateTimeOriginal && (
                    <div className="flex justify-between">
                      <span className="text-muted">Date/Time Original:</span>
                      <span>{new Date(inspection.photo.metadata.capture.dateTimeOriginal).toLocaleString()}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.capture.dateTime && inspection.photo.metadata.capture.dateTime !== inspection.photo.metadata.capture.dateTimeOriginal && (
                    <div className="flex justify-between">
                      <span className="text-muted">Date/Time:</span>
                      <span>{new Date(inspection.photo.metadata.capture.dateTime).toLocaleString()}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.capture.dateTimeDigitized && inspection.photo.metadata.capture.dateTimeDigitized !== inspection.photo.metadata.capture.dateTimeOriginal && (
                    <div className="flex justify-between">
                      <span className="text-muted">Date/Time Digitized:</span>
                      <span>{new Date(inspection.photo.metadata.capture.dateTimeDigitized).toLocaleString()}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.capture.timeZone && (
                    <div className="flex justify-between">
                      <span className="text-muted">Time Zone:</span>
                      <span>{inspection.photo.metadata.capture.timeZone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Settings */}
            {inspection.photo.metadata.settings && Object.keys(inspection.photo.metadata.settings).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Settings</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.settings.whiteBalance && (
                    <div className="flex justify-between">
                      <span className="text-muted">White Balance:</span>
                      <span>{inspection.photo.metadata.settings.whiteBalance}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.settings.colorSpace && (
                    <div className="flex justify-between">
                      <span className="text-muted">Color Space:</span>
                      <span>{inspection.photo.metadata.settings.colorSpace}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.settings.flash && (
                    <div className="flex justify-between">
                      <span className="text-muted">Flash:</span>
                      <span>{inspection.photo.metadata.settings.flash}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.settings.orientation !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">Orientation:</span>
                      <span>{inspection.photo.metadata.settings.orientation}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Location */}
            {inspection.photo.metadata.location && Object.keys(inspection.photo.metadata.location).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Location</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.location.latitude !== undefined && inspection.photo.metadata.location.longitude !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">Coordinates:</span>
                      <span>{inspection.photo.metadata.location.latitude.toFixed(6)}°, {inspection.photo.metadata.location.longitude.toFixed(6)}°</span>
                    </div>
                  )}
                  {inspection.photo.metadata.location.altitude !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted">Altitude:</span>
                      <span>{inspection.photo.metadata.location.altitude.toFixed(2)}m</span>
                    </div>
                  )}
                  {inspection.photo.metadata.location.timestamp && (
                    <div className="flex justify-between">
                      <span className="text-muted">GPS Timestamp:</span>
                      <span>{new Date(inspection.photo.metadata.location.timestamp).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Software */}
            {inspection.photo.metadata.software && Object.keys(inspection.photo.metadata.software).length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted mb-2">Software</div>
                <div className="pl-4 space-y-1 text-sm">
                  {inspection.photo.metadata.software.software && (
                    <div className="flex justify-between">
                      <span className="text-muted">Software:</span>
                      <span>{inspection.photo.metadata.software.software}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.software.artist && (
                    <div className="flex justify-between">
                      <span className="text-muted">Artist:</span>
                      <span>{inspection.photo.metadata.software.artist}</span>
                    </div>
                  )}
                  {inspection.photo.metadata.software.copyright && (
                    <div className="flex justify-between">
                      <span className="text-muted">Copyright:</span>
                      <span className="text-xs">{inspection.photo.metadata.software.copyright}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-surface-2 rounded-lg p-4 text-center text-muted">
            No metadata available
          </div>
        )}
      </section>

      {/* Version History (Feature 081) */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Version History</h3>
          {versions.length >= 2 && (
            <button
              onClick={() => setShowCompare(!showCompare)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                showCompare
                  ? 'bg-primary text-white'
                  : 'bg-surface-2 text-foreground hover:bg-surface-3'
              }`}
            >
              {showCompare ? 'Hide Compare' : 'Compare Versions'}
            </button>
          )}
        </div>
        <div className="bg-surface-2 rounded-lg p-4">
          <VersionHistory
            photoId={photoId}
            onVersionSelect={(version) => {
              logger.info('PhotoInspector', 'Version selected', {
                versionId: version.id,
                versionNumber: version.versionNumber,
              });
            }}
          />
        </div>

        {/* ImageCompare - shown when Compare button clicked (T060) */}
        {showCompare && versions.length >= 2 && (
          <div className="mt-4 bg-surface-2 rounded-lg p-4 h-144">
            <ImageCompare
              versions={versions}
              className="h-full"
            />
          </div>
        )}
      </section>

      {/* Image Variants - with Version column (Feature 081) */}
      <section className="mb-8">
        <h3 className="text-lg font-semibold mb-3">
          Image Variants ({inspection.stats.variantCount})
        </h3>
        {(() => {
          // Sort variants by version (newest first), then by variant type
          const sortedVariants = [...inspection.variants].sort((a, b) => {
            const versionA = a.versionNumber ?? 0;
            const versionB = b.versionNumber ?? 0;
            if (versionB !== versionA) return versionB - versionA;
            // Same version, sort by variant type
            const typeOrder = { thumbnail: 1, small: 2, medium: 3, large: 4, full: 5 };
            return (typeOrder[a.variantType as keyof typeof typeOrder] || 99) -
                   (typeOrder[b.variantType as keyof typeof typeOrder] || 99);
          });

          // Find the highest version number for "Current" label
          const maxVersion = Math.max(...sortedVariants.map(v => v.versionNumber ?? 0));

          // Group consecutive variants by version for visual separation
          let lastVersion: number | null = null;

          return (
            <div className="bg-surface-2 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-1">
                  <tr>
                    <th className="text-left p-3">Version</th>
                    <th className="text-left p-3">Variant</th>
                    <th className="text-left p-3">Dimensions</th>
                    <th className="text-right p-3">Size</th>
                    <th className="text-left p-3">Format</th>
                    <th className="text-left p-3">Storage URL</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVariants.map((variant) => {
                    const versionNum = variant.versionNumber ?? 0;
                    const isNewGroup = lastVersion !== versionNum;
                    lastVersion = versionNum;
                    const isOriginal = versionNum === 0;
                    const isCurrent = versionNum === maxVersion && maxVersion > 0;

                    return (
                      <tr
                        key={variant.id}
                        className={`border-t ${isNewGroup ? 'border-border border-t-2' : 'border-border/50'}`}
                      >
                        <td className="p-3">
                          {isNewGroup && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              isCurrent
                                ? 'bg-primary text-primary-foreground'
                                : isOriginal
                                  ? 'bg-amber-600 text-white'
                                  : 'bg-muted text-muted-foreground'
                            }`}>
                              {isOriginal ? 'Original' : `V${versionNum}`}
                              {isCurrent && ' ✓'}
                            </span>
                          )}
                        </td>
                        <td className="p-3 font-medium">{variant.variantType}</td>
                        <td className="p-3 text-muted">{variant.width} × {variant.height}</td>
                        <td className="p-3 text-right text-muted">
                          {(variant.fileSize / 1024).toFixed(0)} KB
                        </td>
                        <td className="p-3 text-muted">{variant.format}</td>
                        <td className="p-3 text-muted">
                          {variant.storageUrl ? (
                            <a
                              href={variant.storageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline font-mono text-xs truncate block max-w-xs"
                              title={variant.storageUrl}
                            >
                              {variant.storageUrl.split('/').pop()}
                            </a>
                          ) : (
                            <span className="text-muted italic">** MISSING **</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
        <div className="p-3 bg-surface-1 rounded-lg text-right text-sm mt-2">
          <span className="text-muted">Total Storage: </span>
          <span className="font-medium">
            {(inspection.stats.totalFileSize / 1024 / 1024).toFixed(2)} MB
          </span>
        </div>
      </section>

      {/* Collections */}
      <section className="mb-8">
        <h3 className="text-lg font-semibold mb-3">
          Collections ({inspection.stats.collectionCount})
        </h3>
        {inspection.collections.length > 0 ? (
          <div className="bg-surface-2 rounded-lg divide-y divide-border">
            {inspection.collections.map((collection) => (
              <div key={collection.id} className="p-4">
                <div className="font-medium">{collection.name}</div>
                <div className="text-sm text-muted mt-1">
                  Position: {collection.position} • Added: {new Date(collection.addedAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-surface-2 rounded-lg p-4 text-center text-muted">
            Not in any collections
          </div>
        )}
      </section>

      {/* Discussions */}
      <section className="mb-8">
        <h3 className="text-lg font-semibold mb-3">
          Discussions ({inspection.stats.critiqueCount})
        </h3>
        {inspection.critiques.length > 0 ? (
          <div className="bg-surface-2 rounded-lg divide-y divide-border">
            {inspection.critiques.map((critique) => (
              <div key={critique.id} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">@{critique.criticUsername}</span>
                    {critique.editId && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        with edit
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-muted">
                    {new Date(critique.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-sm text-muted line-clamp-2">
                  {critique.content}
                </div>
                {critique.collectionName && (
                  <div className="text-xs text-muted mt-1">
                    in {critique.collectionName}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-surface-2 rounded-lg p-4 text-center text-muted">
            No discussions yet
          </div>
        )}
      </section>

      {/* Stats Summary */}
      <section className="mb-8">
        <h3 className="text-lg font-semibold mb-3">Stats</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-surface-2 rounded-lg p-4">
            <div className="text-2xl font-bold">{inspection.stats.variantCount}</div>
            <div className="text-sm text-muted">Variants</div>
          </div>
          <div className="bg-surface-2 rounded-lg p-4">
            <div className="text-2xl font-bold">{inspection.stats.collectionCount}</div>
            <div className="text-sm text-muted">Collections</div>
          </div>
          <div className="bg-surface-2 rounded-lg p-4">
            <div className="text-2xl font-bold">{inspection.stats.critiqueCount}</div>
            <div className="text-sm text-muted">Discussions</div>
          </div>
          <div className="bg-surface-2 rounded-lg p-4">
            <div className="text-2xl font-bold">
              {(inspection.stats.totalFileSize / 1024 / 1024).toFixed(1)} MB
            </div>
            <div className="text-sm text-muted">Total Size</div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function DevPhotoInspectorPage() {
  const { photoId } = useParams<{ photoId?: string }>();
  const navigate = useNavigate();

  // Fetch current user's photos
  const { photos: myPhotos, loading: isLoading } = usePhotosGraphQL();

  // Convert to PhotoItem2ViewModel format for CollectionAndDetailView2
  const photoItems = myPhotos.map(photo => ({
    photoData: photo,
    critiqueCount: 0, // Not needed for inspector tool
  }));

  const handlePhotoSelect = (photoIds: string[]) => {
    if (photoIds.length > 0) {
      navigate(`/dev/tool/photos/${photoIds[0]}`);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <header className="border-b p-4 bg-surface-1">
        <h1 className="text-2xl font-bold">📸 Photo Inspector</h1>
        <p className="text-sm text-muted mt-1">
          Development tool for inspecting photo metadata, variants, and relationships
        </p>
      </header>

      <div className="flex-1 overflow-hidden">
        <SplitPane direction="horizontal" autoSaveId="photo-inspector">
          <Panel defaultSize={30} minSize={20} maxSize={50}>
            {isLoading ? (
              <div className="p-4 text-muted">Loading photos...</div>
            ) : photoItems.length === 0 ? (
              <div className="p-4 text-muted">No photos found. Upload some photos first!</div>
            ) : (
              <CollectionAndDetailView2
                photos={photoItems}
                collectionPosition="left"
                layout="vertical"
                itemTemplate="photo-only"
                selectionMode="single"
                selectedIds={photoId ? [photoId] : []}
                onSelectionChange={handlePhotoSelect}
                hideDetail={true}
              />
            )}
          </Panel>

          <PanelResizeHandle className="w-1 bg-border hover:bg-primary transition-colors" />

          <Panel>
            {photoId ? (
              <PhotoDetailView photoId={photoId} />
            ) : (
              <PhotoDetailEmpty />
            )}
          </Panel>
        </SplitPane>
      </div>
    </div>
  );
}
